<template>
  <div class="c-config-content">
    <h4 class="c-config-content__title">{{ $t('route') }}</h4>
    <div v-if="firstLoading">
      <skeleton-loading>
        <row>
          <square-skeleton :boxProperties="{bottom: '10px', height: '30px'}"></square-skeleton>
        </row>
        <row>
          <square-skeleton :boxProperties="{bottom: '10px', height: '200px'}"></square-skeleton>
        </row>
      </skeleton-loading>
    </div>
    <div v-else>
      <div class="c-template-table table-responsive c-role-table">
        <table class="table table-hover">
          <thead>
          <tr>
            <th>{{ $t('phoneNumber') }} ({{itemCount}})</th>
            <th>{{ $t('inbound') }}</th>
            <th>{{ $t('outbound') }}</th>
            <th class="th-80 text-right"></th>
          </tr>
          </thead>
          <tbody v-if="listLoading">
            <tr>
              <td colspan="4" class="text-center"><b-spinner></b-spinner></td>
            </tr>
          </tbody>
          <tbody v-else-if="items && items.length">
          <tr v-for="item in items" :key="item._id">
            <td>
              <b>{{ item.number }}</b>&nbsp;
              <span class="badge badge-primary">{{ item.network.name }}</span>&nbsp;
              <span v-if="item.isRental" class="badge badge-success" v-b-tooltip.hover.top="$t('rentalNumber')">*</span>
            </td>
            <td>
              <p>
                <span v-if="!item.inbound" class="badge badge-danger">{{ $t('inboundDisabled') }}</span>
                <span v-if="item.inbound" class="badge badge-success">{{ $t('inboundEnabled') }}</span>&nbsp;
                <span v-if="item.inbound && item.inboundFollow" class="badge badge-warning" 
                v-b-tooltip.hover.top="item.inboundFollowBy === 'contact' ? $t('inboundFollowByContact') : $t('inboundFollowByLastCall')">*</span>
              </p>
              <p v-if="item.inbound">
                <b v-if="item.inboundTarget">{{ renderInboundTarget(item.inboundTarget) }}</b>
                <b v-else>{{$t('all')}}</b>
              </p>
            </td>
            <td>
              <p>
                <span v-if="item.outbound" class="badge badge-success">{{ $t('outboundEnabled') }}</span>
                <span v-if="!item.outbound" class="badge badge-danger">{{ $t('outboundDisabled') }}</span>&nbsp;
                <label v-if="item.isPackage" class="badge badge-primary" v-b-tooltip.hover.top="$t('monthlyUser')">{{item.packageExts.length}}/{{item.packageLimit}}</label>
              </p>
              <template v-if="item.outbound">
                <p><span>{{ $t('outboundFrom') }}: </span><b>{{ renderOutBoundFrom(item) }}</b></p>
                <p><span>{{ $t('outboundTo') }}: </span><b>{{ renderOutBoundTo(item) }}</b></p>
                <p v-if="item.backupNumber"><span>{{$t('backupNumber')}}: </span><b>{{renderBackupNumber(item.backupNumber)}}</b></p>
                <p v-if="item.backupNumber2"><span>{{$t('backupNumber2')}}: </span><b>{{renderBackupNumber(item.backupNumber2)}}</b></p>
                <p><span>{{ $t('priority') }}: </span><b>{{ item.priority }}</b></p>
                <p v-if="item.isRental"><span>{{$t('netCallFee')}}</span>: <b>{{item.privateFee ? item.netCallFee : item.network.netCallFee}} {{$store.state.user.client.country.currency}}/{{$t('minute')}}</b></p>
                <p v-if="item.isRental"><span>{{$t('offnetCallFee')}}</span>: <b>{{item.privateFee ? item.offnetCallFee : item.network.offnetCallFee}} {{$store.state.user.client.country.currency}}/{{$t('minute')}}</b></p>
              </template>
            </td>
            <td class="text-right">
              <button class="btn btn-outline-gray btn-sm is-border-1 mr-1" type="button" v-if="item.isPackage"
                      @click="$refs.updateExt.update(item._id)" v-b-tooltip="$t('updatePackageExt')"
                      :disabled="updateExt.loading">
                <b-spinner small v-if="updateExt.loading"></b-spinner>
                <i v-else class="icon12-equalizer-primary"></i>
              </button>
              <button @click="update(item._id)" :disabled="updateLoading" class="btn btn-outline-gray btn-sm is-border-1"
                      type="button" v-b-tooltip="$t('updateRoute')">
                <b-spinner v-if="updateLoading" small></b-spinner>
                <i v-else class="icon12-edit-primary"></i>
              </button>
            </td>
          </tr>
          <update-ext ref="updateExt" :loading-model="updateExt"></update-ext>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="4" class="text-center">{{$t('noPhoneNumberSetUp')}}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="c-paging" v-if="itemCount > filterFormModel.pageSize">
        <b-pagination size="sm" class="pagination pagination-sm justify-content-center"
                      v-model="filterFormModel.page" :total-rows="itemCount"
                      :per-page="filterFormModel.pageSize" @input="load">
        </b-pagination>
      </div>
      <b-modal v-model="showForm" :title="$t('route') + ': ' + formModel.number" @ok="save" no-close-on-backdrop size="lg"
               no-enforce-focus>
        <div class="row">
          <div class="col">
            <h4 v-if="formModel.inbound">{{ $t('inboundSetting') }}</h4>
            <template v-if="formModel.inbound">
              <form-checkbox :model="formModel" attribute="recordInbound" :chk-label="$t('recordInbound')"></form-checkbox>
              <form-checkbox :model="formModel" attribute="inboundFollow" :chk-label="$t('inboundFollow')"></form-checkbox>
              <form-select v-if="formModel.inboundFollow" :model="formModel" :errors="formErrors" :options="inboundFollowBys" attribute="inboundFollowBy"
                           :label="$t('inboundFollowBy')"></form-select>
              <form-input v-if="formModel.inboundFollow" :model="formModel" attribute="inboundFollowTimeout" :errors="formErrors" :label="$t('inboundFollowTimeout')"></form-input>
              <target-select :model="formModel" attribute="inboundTarget" :errors="formErrors" :label="$t('routeInboundTo')"></target-select>
            </template>
            <h4 v-if="formModel.outbound">{{ $t('outboundSetting') }}</h4>
            <template v-if="formModel.outbound">
              <form-checkbox :model="formModel" attribute="recordOutbound" :chk-label="$t('recordOutbound')"></form-checkbox>
              <form-select :model="formModel" attribute="outboundFromType" :errors="formErrors" :options="outboundFromTypes" :label="$t('outboundFromType')"></form-select>
              <form-checkbox-group
                  v-if="formModel.outboundFromType ==='group' && outboundFromGroups.length > 0"
                  :model="formModel"
                  attribute="outboundFromGroups"
                  errors="formErrors"
                  :label="$t('outboundFromGroups')"
                  :options="outboundFromGroups">
              </form-checkbox-group>
              <form-input v-if="formModel.outboundFromType ==='custom'" :model="formModel" attribute="outboundFromPattern" :errors="formErrors" :label="$t('pattern')"></form-input>
              <form-select :model="formModel" attribute="outboundToType" :errors="formErrors" :options="outboundToTypes" :label="$t('outboundToType')"></form-select>
              <form-input v-if="formModel.outboundToType ==='custom'" :model="formModel" attribute="outboundToPattern" :errors="formErrors" :label="$t('pattern')"></form-input>
              <form-checkbox-group
                  v-if="formModel.outboundToType ==='network'"
                  :model="formModel"
                  attribute="outboundToNetworks"
                  errors="formErrors"
                  :label="$t('outboundToNetworks')"
                  :options="outboundToNetworks">
              </form-checkbox-group>
              <form-select :model="formModel" :errors="formErrors" :options="backupNumberList" attribute="backupNumber"
                           :label="$t('backupNumber')"></form-select>
              <form-select :model="formModel" :errors="formErrors" :options="backupNumberList" attribute="backupNumber2"
                           :label="$t('backupNumber2')"></form-select>
            </template>
            <span v-else class="badge badge-danger">{{ $t('outboundDisabled') }}</span>
            <hr>
            <form-input :model="formModel" attribute="priority" :errors="formErrors" :label="$t('priority')"></form-input>
          </div>
        </div>
        <template v-slot:modal-footer>
          <b-button variant="secondary" class="float-right" @click="showForm = false">{{ $t('cancel') }}
          </b-button>
          <b-button :disabled="saveLoading" variant="primary" class="float-right" @click="save">
            <b-spinner v-if="saveLoading" small></b-spinner>
            {{ $t('save') }}
          </b-button>
        </template>
      </b-modal>
    </div>
  </div>
</template>

<script>
import list from '../../../../mixins/List';
import crud from '../../../../mixins/CRUD';
import TargetSelect from "../../../../components/TargetSelect";
import UpdateExt from "./UpdateExt";

export default {
  mixins: [list, crud],
  components: {UpdateExt, TargetSelect},
  data: function () {
    return {
      service: '/number',
      reloadBus: 'reloadNumberList',
      autoLoad: true,
      defaultFormModel: {},
      outboundFromTypes: [
        {value: 'all', text: this.$t('all')},
        {value: 'group', text: this.$t('group')},
        {value: 'custom', text: this.$t('custom')},
      ],
      inboundFollowBys: [
        {value: null, text: this.$t('chooseInboundFollowBy')},
        {value: 'contact', text: this.$t('inboundFollowByContact')},
        {value: 'lastcall', text: this.$t('inboundFollowByLastCall')},
      ],
      outboundFromGroups: [],
      outboundToNetworks: [],
      outboundToTypes: [
        {value: 'all', text: this.$t('all')},
        {value: 'network', text: this.$t('network')},
        {value: 'custom', text: this.$t('custom')},
      ],
      backupNumberList: [{value: null, text: this.$t('chooseBackupNumber')}],
      updateExt: {
        loading: false
      }
    }
  },
  methods: {
    renderInboundTarget(inboundTarget) {
      if (/^1[0-9]{2}$/.test(inboundTarget)) {
        return this.$t('ivr') + ' (' + inboundTarget + ')';
      }
      if (/^2[0-9]{2}$/.test(inboundTarget)) {
        return this.$t('queue') + ' (' + inboundTarget + ')';
      }
      if (/^3[0-9]{2}$/.test(inboundTarget)) {
        return this.$t('timeCondition') + ' (' + inboundTarget + ')';
      }
      if (/^4[0-9]{2}$/.test(inboundTarget)) {
        return this.$t('announcement') + ' (' + inboundTarget + ')';
      }
      if (/^5[0-9]{2}$/.test(inboundTarget)) {
        return this.$t('apiRoute') + ' (' + inboundTarget + ')';
      }
      if (/^[1-9][0-9]{3}$/.test(inboundTarget)) {
        return this.$t('group') + ' (' + inboundTarget + ')';
      }
      if (/^[1-9][0-9]{4,5}$/.test(inboundTarget)) {
        return this.$t('ext') + ' (' + inboundTarget + ')';
      }
      if (/^[0-9]{7,13}$/.test(inboundTarget)) {
        return this.$t('externalNumber') + ' (' + inboundTarget + ')';
      }
    },
    renderOutBoundFrom(item) {
      switch (item.outboundFromType) {
        case 'all':
          return this.$t('outboundTypeAll');
        case 'group':
          return this.$t('group') + ': ' + this.outboundFromGroups.filter(i => item.outboundFromGroups.includes(i.value)).map(i => i.text).join(', ');
        case 'custom':
          return item.outboundFromPattern;
        default:
          break;
      }
    },
    renderOutBoundTo(item) {
      switch (item.outboundToType) {
        case 'all':
          return this.$t('outboundTypeAll');
        case 'network':
          return this.$t('network') + ': ' + this.outboundToNetworks.filter(i => item.outboundToNetworks.includes(i.value)).map(i => i.text).join(', ');
        case 'custom':
          return item.outboundToPattern;
        default:
          break;
      }
    },
    renderBackupNumber(backupNumberId) {
      if (this.items && this.items.length) {
        let backupNumber = this.items.find(i => i._id === backupNumberId);
        if (backupNumber) {
          return backupNumber.differenceInOutNumbers ? backupNumber.outNumber : backupNumber.number
        }
        return this.$t('na')
      }
      return this.$t('na')
    },
    update: function (id) {
      let uri = this.service;
      if (id) {
        uri += '/' + id;
      }
      this.updateLoading = true;
      this.getBackupNumberList(id);
      this.$service.get(uri).then(response => {
        this.formModel = {};
        this.formTitle = this.$t('update');
        this.formErrors = [];
        this.showForm = true;
        this.formModel = response.data;
        this.formModel.id = id;
      }).finally(() => {
        this.updateLoading = false;
      });
    },
    getBackupNumberList(id) {
      this.backupNumberList = [{value: null, text: this.$t('chooseBackupNumber')}]
      if (this.items && this.items.length) {
        this.items.forEach(i => {
          if (i._id !== id && i.outbound) {
            this.backupNumberList.push({value: i._id, text: i.differenceInOutNumbers ? i.outNumber : i.number})
          }
        })
      }
    },
  },
  mounted() {
    this.outboundFromGroups = [];
    this.$bgService.get('/user-group', {
      params: {
        page: 1,
        pageSize: 1000,
        status: 0
      }
    }).then(response => {
      response.data.docs.forEach(item => {
        this.outboundFromGroups.push({value: item._id, text: item.ext + ' (' + item.name + ')'});
      });
    });
    this.outboundToNetworks = [];
    this.$bgService.get('/phone-network', {
      params: {
        page: 1,
        pageSize: 1000,
        status: 0
      }
    }).then(response => {
      response.data.docs.forEach(item => {
        if (item.numberPrefixes.length > 0) {
          this.outboundToNetworks.push({value: item._id, text: item.name});
        }
      });
    });
  },
}
</script>